import { DOCUMENT } from '@angular/common';
import { AfterViewChecked, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { GridType, GridsterConfig, GridsterItem } from 'angular-gridster2';
import { MILL_SECONDS } from 'common/constant/general';
import { MB_RESPONSE, MB_TYPE, MessageBoxComponent, MessageBoxOptions } from 'components/message-box/message-box.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { AuthenticationService, AuthenticationState } from 'services/authentication/authentication.service';
import {
  DashboardRequestStatus,
  DashboardService,
  IDashboardItem,
  IDashboardListItem
} from 'services/dashboard';
import { NodeTypes } from "services/definition/class/node-types";
import { DefinitionService } from 'services/definition/definition.service';
import { SourceDefinitionStatus, TreeNodeType } from "services/definition/enum";
import { IDeviceNode, IStorageTreeNode, ITelemetryNode, ITreeNode } from "services/definition/interface";
import { SpinnerTypes } from 'services/loading/spinner/spinner-type';
import { ConnectionState } from 'services/socket/classes/connection-state';
import { SocketService } from 'services/socket/socket.service';
import { PreferredSettings, StorageService } from 'services/storage/storage.service';
import { Logger } from 'src/app/common/utility/logger/logger';
import { SubscriptionService } from 'src/app/services/subscription/subscription.service';
import { DTCSettings, DTCWidgetSettingsComponent } from 'src/app/widgets/dtc-widget/dtc-widget-settings/dtc-widget-settings.component';
import { LineWidgetSettings } from 'src/app/widgets/line-widget/line-widget-common';
import { LineWidgetSettingsComponent } from 'src/app/widgets/line-widget/line-widget-settings/line-widget-settings.component';
import { ListSettings, ListWidgetSettingsComponent } from 'src/app/widgets/list-widget/list-widget-settings/list-widget-settings.component';
import { Strings } from '../../common/utility/string/string';

// Constants
const DASHBOARD_VERSION = 1
// Loading timeout
const MAX_TIMEOUT = 60;

const WidgetTypes = {
  LineWidget: 'line-widget',
  ListWidget: 'list-widget',
  DTCWidget: 'dtc-widget'
}

// Interfaces
export interface Widgets {
  type: string
  settings: ListSettings | DTCSettings | LineWidgetSettings | any
  gridsterItem: GridsterItem
}

// Interfaces
interface MenuItems {
  id: any;
  label: string;
  icon: string;
}

const enum DashboardMenuId {
  Add = 1,
  Edit = 2,
  Delete = 3
}

@Component({
  selector: 'ems-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})

export class DashboardComponent implements OnInit, OnDestroy, AfterViewChecked {
  private unsubscribe: Subject<void> = new Subject();
  // Load this from the backend
  public dashboardList!: Array<IDashboardListItem>;
  public currentDashboard!: IDashboardItem | undefined;
  public selectedDashboardId!: number | undefined;
  private pendingDashboardCreate: boolean = false;
  private dashboardEditBackup!: IDashboardItem | undefined;

  public gridsterOptions!: GridsterConfig;
  private addMode: boolean = false;
  public editMode: boolean = false;
  public showWidgetLabels: boolean = true;

  public isEditDeleteEnabled(id: number): boolean {
    return this.selectedDashboardId === undefined && (id === DashboardMenuId.Edit || id === DashboardMenuId.Delete)
  }

  // Initialize dashboard menus
  public elipseMenu: MenuItems[] = [
    { id: DashboardMenuId.Add, label: 'dashboard.dot-menu.add', icon: "add" },
    { id: DashboardMenuId.Edit, label: 'dashboard.dot-menu.edit', icon: 'edit' },
    { id: DashboardMenuId.Delete, label: 'dashboard.dot-menu.delete', icon: 'delete' }
  ]

  public widgetMenu: MenuItems[] = [
    { id: WidgetTypes.LineWidget, label: 'dashboard.labels.submenu.line-widget', icon: "show_chart" },
    { id: WidgetTypes.ListWidget, label: 'dashboard.labels.submenu.list-widget', icon: "view_list" },
    { id: WidgetTypes.DTCWidget, label: 'dashboard.labels.submenu.dtc-widget', icon: "monitor_heart" },
  ]

  constructor(
    @Inject(DOCUMENT) private document: any,
    private dialog: MatDialog,
    private dashboardService: DashboardService,
    private subscriptionService: SubscriptionService,
    private definitionService: DefinitionService,
    private socketService: SocketService,
    private authenticationService: AuthenticationService,
    private spinner: NgxSpinnerService,
  ) {
    this.listenForDashboardUpdates();
    this.listenForSourceDefinitionComplete();
    this.listenForAuthenticationState();

  } // constructor
  private elem: any;

  public ngOnInit(): void {
    this.elem = document.getElementById('full');
    this.startSpinner()

    // Gridster Initialization
    this.gridsterOptions = {
      gridType: GridType.ScrollVertical,
      draggable: {
        delayStart: 0,
        enabled: false,
        ignoreContentClass: 'gridster-item-content',
        ignoreContent: true,
        dragHandleClass: 'drag-handler',
        stop: undefined,
        start: undefined,
        dropOverItems: true,
        dropOverItemsCallback: undefined
      },
      resizable: {
        enabled: false,
      },
      swap: true,
      pushItems: true,
      minRows: 100,
      maxRows: 100,
      minCols: 100,
      maxCols: 100,
      minItemCols: 10,
      maxItemCols: 100,
      minItemRows: 10,
      maxItemRows: 100,
      maxItemArea: 100 * 100,
      margin: 5,
      defaultItemCols: 20,
      defaultItemRows: 20,
      enableEmptyCellDrop: true,
      dragEnabled: false,
      resizeEnabled: false,

      emptyCellDropCallback: this.emptyCellClick.bind(this),
    };

    if (this.dashboardService?.isDashboardsReady && DefinitionService.SourcesListReady) {
      this.loadDashboards()
    } else {
      this.startSpinner()
    }
  } // ngInit()

  ngOnDestroy(): void {
    this.stopSpinner()
    this.unsubscribe.next();
    this.unsubscribe.complete();
  } // ngOnDestroy()

  ngAfterViewChecked(): void {
    if (this.subscriptionService.getSubscriptionRequestCount > 0
      && this.subscriptionService.getSubscriptionRequestCount >= this.getDashboardSubscriptionsCount) {
      this.subscriptionService.sendSubscription()
    }
  } // ngAfterViewChecked()

  private listenForAuthenticationState(): void {
    combineLatest([this.authenticationService.listenForAuthenticationState, this.socketService.ConnectionState]).
      pipe(takeUntil(this.unsubscribe)).subscribe(([authState, connectState]) => {
        if (authState === AuthenticationState.Authenticated) {
          if (StorageService.getUserPreferredSettings(PreferredSettings.WidgetMenuShowLabels) === undefined) {
            this.showWidgetLabels = true;
            StorageService.setUserPreferredSettings(PreferredSettings.WidgetMenuShowLabels, this.showWidgetLabels);
          } else {
            this.showWidgetLabels = JSON.parse(StorageService.getUserPreferredSettings(PreferredSettings.WidgetMenuShowLabels));
          }
        }

        if (authState === AuthenticationState.NotAuthenticated || connectState !== ConnectionState.Connected) {
          this.selectedDashboardId = this.currentDashboard = undefined;
          this.dashboardList = [];
        }

        if (connectState === ConnectionState.Disconnected) {
          Logger.debug("Dashboard Connection Lost, Resend")
        }
      });
  } // listenForAuthenticationState()

  private listenForSourceDefinitionComplete(): void {
    this.definitionService.listenForSourceDefinitionStatus.pipe(takeUntil(this.unsubscribe)).subscribe(msg => {
      if (msg.status === SourceDefinitionStatus.Success && this.dashboardService.isDashboardsReady) {
        // If the Dashboards are ready too
        this.loadDashboards();
      }
    }); // subscribe()
  } // listenForSourceDefinitionComplete()

  private listenForDashboardUpdates(): void {
    // Listen for the Dashboard Service events
    this.dashboardService.dashboardUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(status => {
      // Handle Dashboard Reload Request result
      if (status.status === DashboardRequestStatus.Reloaded && DefinitionService.SourcesListReady) {
        // If the SourceDefinitions are ready too
        this.loadDashboards()
      }
      // Handle Dashboard Create Request result
      if (this.pendingDashboardCreate) {
        if (status.status == DashboardRequestStatus.Success) {
          this.dashboardList = this.dashboardService.getDashboardList;
          this.currentDashboard = this.storageToDashboard(<IDashboardItem>this.dashboardService.getDashboardById(status.data));
          this.selectedDashboardId = this.currentDashboard !== undefined ? this.currentDashboard.id : undefined;
          this.stopSpinner()
        } else {
          Logger.error("listenForDashboardUpdates unknown response")
          // Reload because something went wrong
          this.dashboardService.reloadDashboards();
          this.startSpinner()
        }
        this.pendingDashboardCreate = false;
      }
    });
  } // listenForDashboardUpdates()

  private loadDashboards(): void {
    this.dashboardList = this.dashboardService.getDashboardList;
    this.currentDashboard = this.dashboardService.getFirstDashboard ?
      this.storageToDashboard(this.dashboardService.getFirstDashboard) : undefined;
    this.selectedDashboardId = this.currentDashboard !== undefined ? this.currentDashboard.id : undefined;

    this.stopSpinner()
  } // launchDashboard()

  private get getDashboardSubscriptionsCount(): number {
    if (this.currentDashboard && this.currentDashboard.widgets) {
      let cnt = 0
      this.currentDashboard.widgets.forEach(widget => {
        if ([WidgetTypes.DTCWidget, WidgetTypes.ListWidget].includes(widget.type)) {
          cnt++
        }
      })
      return cnt
    }

    return 0
  } // getDashboardSubscriptionsCount()


  public toggleEditMode() {
    this.editMode = !this.editMode;
    this.gridsterOptions['resizable'] = { enabled: this.editMode };
    this.gridsterOptions['draggable'] = { enabled: this.editMode };
    this.gridsterOptions['dragEnabled'] = { enabled: this.editMode };
    this.gridsterOptions['resizeEnabled'] = { enabled: this.editMode };

    this.changedOptions();
  } // toggleEditMode()

  // Show/Hide Not Configured Message
  public showNotConfigured(): boolean {
    return !this.currentDashboard || !this.currentDashboard.widgets.length;
  } // showNotConfigured()

  // Show/Hide text
  public showWidgetMenuLabels() {
    this.showWidgetLabels = !this.showWidgetLabels;
    StorageService.setUserPreferredSettings(PreferredSettings.WidgetMenuShowLabels, this.showWidgetLabels);
    window.dispatchEvent(new Event('resize'));
  } // showWidgetMenuLabels()

  private changedOptions(): void {
    if (this.gridsterOptions.api && this.gridsterOptions.api.optionsChanged) {
      this.gridsterOptions.api.optionsChanged();
    }
  } // changedOptions()

  public dashboardSelectionChanged() {
    this.subscriptionService.resetSubscription();
    let found = <IDashboardItem>this.dashboardList.
      find((dashboard) => dashboard.id === this.selectedDashboardId)
    this.currentDashboard = this.storageToDashboard(<IDashboardItem>this.dashboardService.getDashboardById(found.id));
  } // dashboardSelectionChanged()

  public clickToolbarSaveDashboard(dashboardName: string): void {
    this.toggleEditMode();

    // Adding Dashboard
    if (this.addMode && this.currentDashboard) {
      this.addMode = false;
      this.currentDashboard.name = dashboardName;
      // The createDashboard will push on success
      this.startSpinner()
      this.pendingDashboardCreate = true;
      this.dashboardService.createDashboard(this.dashboardToStorage(structuredClone(this.currentDashboard)));
    } else if (this.currentDashboard) { // Edit Mode
      this.currentDashboard.name = dashboardName;
      this.currentDashboard.version = DASHBOARD_VERSION
      this.dashboardService.saveDashboard(this.dashboardToStorage(structuredClone(this.currentDashboard)))
      let found = this.dashboardList.find(dashboard => dashboard.id === this.selectedDashboardId)
      if (found) {
        found.name = this.currentDashboard.name;
      }
    }
  } // clickToolbarSaveDashboard()

  private storageToDashboard(dashboard: IDashboardItem): IDashboardItem {
    dashboard.widgets.forEach(widget => {
      if (widget.type !== WidgetTypes.DTCWidget) {
        widget.settings.sourceNodes = DashboardComponent.storageToTree(widget.settings.sourceNodes, DefinitionService.getSourcesList())
      }
    })

    return dashboard
  } // storageToDashboard()

  private dashboardToStorage(dashboard: IDashboardItem): IDashboardItem {
    dashboard.widgets.forEach(widget => {
      if (widget.type !== WidgetTypes.DTCWidget) {
        widget.settings.sourceNodes = DashboardComponent.treeToStorage(widget.settings.sourceNodes)
      }
    });

    return dashboard
  } // dashboardToStorage()

  public static storageToTree(stored: IStorageTreeNode[], full: ITreeNode[]): ITreeNode[] {
    if (!stored.length || !full.length)
      return []

    const result: ITreeNode[] = []

    stored.forEach((node) => {
      // see if clean node has this node
      const found = full.find((element) => node.name == element.name)

      if (NodeTypes.getNodeType(found) === TreeNodeType.Device) {
        (<IDeviceNode>found).children = DefinitionService.getDeviceChildrenFromLTMI(
          (<IDeviceNode>found).ltmi, undefined, undefined, undefined, (<IDeviceNode>found).arrays, undefined)
      }

      if (node.children && (found && found.children)) {
        found.children = this.storageToTree(node.children, found.children);
        // Flag the parent
        found.indeterminate = found.children.some(child => child.selected || child.indeterminate);
        result.push(found);
      } else { // Possibly Leaf
        if (!node.children && found) {
          found.selected = true;
          (<ITelemetryNode>found).settings = (<ITelemetryNode>node).settings
          result.push(found);
          return result;
        } else { // Error/Mismatch
          // If Node is not found, then discard the node
          return result;
        } // if/else Leaf
      } // if/else (node.children/found.children)
      return result;
    }) // selected.forEach    return nodes;

    return result;
  } // storageToTree()

  public static treeToStorage(nodes: ITreeNode[]): IStorageTreeNode[] {
    const result: IStorageTreeNode[] = [];

    nodes.forEach((node) => {
      const storageNode: IStorageTreeNode = {
        name: node.name
      };
      if ((<ITelemetryNode>node).settings)
        storageNode['settings'] = (<ITelemetryNode>node).settings
      if (node.children?.length)
        storageNode['children'] = node.children

      if (node.selected || node.indeterminate) {
        const tmp: IStorageTreeNode = {
          name: node.name
        };
        if ((<ITelemetryNode>node).settings)
          tmp['settings'] = (<ITelemetryNode>node).settings
        if (node.children?.length)
          tmp['children'] = node.children

        if (node.children != undefined && node.children.length > 0) {
          tmp.children = DashboardComponent.treeToStorage(node.children)
        }
        result.push(tmp)
      }
    });

    return result;
  } // treeToStorage()


  public clickToolbarCancelDashboard(): void {
    // Restore the currently selected dashboard
    if (this.addMode) {
      if (this.selectedDashboardId) {
        this.currentDashboard = this.storageToDashboard(<IDashboardItem>this.dashboardService.getDashboardById(this.selectedDashboardId));
      } else {
        this.currentDashboard = this.dashboardService.getFirstDashboard ?
          this.storageToDashboard(<IDashboardItem>this.dashboardService.getFirstDashboard) : undefined;
      }
      this.addMode = false;
    } else
      if (this.editMode) {
        const ndx = this.dashboardList.findIndex((dashboard) => dashboard.id == this.selectedDashboardId);
        if (this.dashboardEditBackup) {
          this.dashboardList[ndx] = this.dashboardEditBackup;
          this.currentDashboard = this.dashboardEditBackup;
        }
      }
    this.toggleEditMode();
  } // clickToolbarCancelDashboard()

  public get currentDashboardName(): string {
    return this.currentDashboard?.name ?? Strings.Empty;
  } // currentDashboardName

  public toolbarElipseChange(selected: MenuItems) {
    // Elipse Button Selected
    switch (selected.id) {
      case 1: // Add mode
        this.subscriptionService.resetSubscription();
        this.addMode = true;
        // Init temporary Dashboard
        this.currentDashboard = structuredClone({
          id: 0,
          name: '',
          version: DASHBOARD_VERSION,
          widgets: []
        });
        this.toggleEditMode();
        break;
      case 2: // Edit mode
        // Not valid unless dashboard exists
        if (!this.selectedDashboardId)
          return;
        // Copy Dashboard for edit
        this.dashboardEditBackup = structuredClone(this.currentDashboard)
        this.addMode = false;
        this.toggleEditMode();
        break;
      case 3: // Delete
        // Not valid unless dashboard exists
        if (!this.selectedDashboardId)
          return;

        // Confirm Delete
        const msgBoxSettings: MessageBoxOptions = {
          type: MB_TYPE.YES_NO,
          title: undefined,
          message: "dashboard.labels.delete-dashboard"
        }
        const msgBox = this.dialog.open(MessageBoxComponent, {
          data: msgBoxSettings
        });
        msgBox.afterClosed().subscribe((res) => {
          if (res.event === MB_RESPONSE.YES) {
            this.subscriptionService.resetSubscription();
            if (this.currentDashboard) {
              const ndx = this.dashboardList.findIndex(item => item.id === this.currentDashboard?.id)
              this.dashboardList.splice(ndx, 1);
              this.dashboardService.deleteDashboard(this.currentDashboard);
            }
            // Find first dashboard that isn't being deleted, if it exists
            if (this.dashboardList.length) {
              this.selectedDashboardId = this.dashboardList[0].id
              this.currentDashboard = this.storageToDashboard(<IDashboardItem>this.dashboardService.getDashboardById(this.selectedDashboardId));
            } else {
              this.selectedDashboardId = undefined;
              this.currentDashboard = undefined;
            }
          }
        });
        break;
      default: // Unknown selection
        break;
    } // switch
  } // toolbarElipseChange()

  public deleteWidget(item: any) {
    if (this.currentDashboard) {
      this.currentDashboard.widgets.splice(this.currentDashboard.widgets.indexOf(item), 1);
      this.changedOptions()
    }
  } // deleteWidget()

  // Add Widget to the dashboard
  public clickWidgetMenu(item: any) {
    // Witch Item Selected?
    if (this.currentDashboard) {
      switch (item.id) {
        case WidgetTypes.LineWidget: // Line Chart
          this.currentDashboard.widgets.push({
            type: WidgetTypes.LineWidget,
            settings: structuredClone(LineWidgetSettingsComponent.getDefault()),
            gridsterItem: { cols: 30, rows: 20, x: item.x, y: item.y, minItemRows: 5, minItemCols: 5 }
          });
          break;
        case WidgetTypes.ListWidget: // List Widget
          this.currentDashboard.widgets.push({
            type: WidgetTypes.ListWidget,
            settings: structuredClone(ListWidgetSettingsComponent.getDefault()),
            gridsterItem: { cols: 20, rows: 20, x: item.x, y: item.y, minItemRows: 5, minItemCols: 5 }
          });
          break;
        case WidgetTypes.DTCWidget: // DTC Widget
          this.currentDashboard.widgets.push({
            type: WidgetTypes.DTCWidget,
            settings: structuredClone(DTCWidgetSettingsComponent.getDefault()),
            gridsterItem: { cols: 40, rows: 20, x: item.x, y: item.y, minItemRows: 5, minItemCols: 20 }
          });
          break;
        default:
          Logger.error("Invalid Widget selection: ", item.id);
          break;
      } // switch
    } // this.currentDashboard

  } // clickWidgetMenu()

  // Drag cursor style
  public get cursorStyle(): string {
    return this.editMode ? "cursor: move;" : ""
  } // get cursorStyle()

  // Gridster Handlers
  // Dragging using Drag Icon
  public dragStartHandler(ev: DragEvent, item: number): void {
    if (ev.dataTransfer) {
      ev.dataTransfer.setData('text/plain', 'Drag Me Button');
      ev.dataTransfer.dropEffect = 'copy';
    }
  } // dragStartHandler()

  // Detects Widget Drag start to know which one on drop
  public dragstart(item: MenuItems) {
    this.chartType = item.id;
  } // dragstart()

  private chartType!: string;
  // Drag-n-Drop Add Widget to the dashboard
  private emptyCellClick(event: MouseEvent, item: GridsterItem): void {
    if (this.currentDashboard) {
      switch (this.chartType) {
        case WidgetTypes.LineWidget: // Line Chart
          // Add the widget to the Dashboard
          this.currentDashboard.widgets.push({
            type: this.chartType,
            settings: structuredClone(LineWidgetSettingsComponent.getDefault()),
            gridsterItem: { cols: 30, rows: 20, x: item.x, y: item.y, minItemRows: 5, minItemCols: 5 }
          });
          break;
        case WidgetTypes.ListWidget: // List Widget
          this.currentDashboard.widgets.push({
            type: this.chartType,
            settings: structuredClone(ListWidgetSettingsComponent.getDefault()),
            gridsterItem: { cols: 20, rows: 20, x: item.x, y: item.y, minItemRows: 5, minItemCols: 5 }
          });
          break;
        case WidgetTypes.DTCWidget: // DTC Widget
          this.currentDashboard.widgets.push({
            type: this.chartType,
            settings: structuredClone(DTCWidgetSettingsComponent.getDefault()),
            gridsterItem: { cols: 40, rows: 20, x: item.x, y: item.y, minItemRows: 5, minItemCols: 20 }
          });
          break;
        default: // Unknown
          return;
      } // switch()
    }
  } // emptyCellClik()

  public isLineWidget(type: string): boolean {
    return type === WidgetTypes.LineWidget
  }

  public isDTCWidget(type: string): boolean {
    return type === WidgetTypes.DTCWidget
  }

  public isListWidget(type: string): boolean {
    return type === WidgetTypes.ListWidget
  }

  private loadingId = 0;
  private loadingTimeout = MAX_TIMEOUT;

  private startSpinner(): void {
    this.spinner.show(this.spinnerName)
    this.loadingTimeout = MAX_TIMEOUT
    if (this.loadingId > 0) {
      return
    }
    this.loadingId = window.setInterval(() => {
      if (this.loadingTimeout > 0) {
        this.loadingTimeout--
        if (this.loadingTimeout && !this.loadingTimeout) {
          this.spinner.hide(this.spinnerName)

          const message: string = !this.dashboardService.isDashboardsReady
            ? "Dashboards not loaded"
            : DefinitionService.SourcesListReady ?
              "Unknown" :
              "Source Definitions not retrieved"

            Logger.warning(`Dashboard Timeout: ${message}`)

            // Tell the user we timed out
            const msgBoxSettings: MessageBoxOptions = {
              type: MB_TYPE.YES_NO,
              title: undefined,
              message: "dashboard.loading-timeout"
            }
            const msgBox = this.dialog.open(MessageBoxComponent, {
              data: msgBoxSettings
            });
            msgBox.afterClosed().subscribe((res) => {
              if (res?.event === MB_RESPONSE.YES) {
                this.loadingTimeout = MAX_TIMEOUT;
                this.spinner.show(this.spinnerName)
              } else {
                this.spinner.hide(this.spinnerName);
                window.clearInterval(this.loadingId)
                this.loadingId = 0;
              }
            }); // afterClosed subscribe
          }
        }
        // Catch if system completed before user response
        if (this.loadingId > 0 && this.dashboardService.isDashboardsReady && DefinitionService.SourcesListReady) {
          this.stopSpinner()
        }
      }, MILL_SECONDS) // setInterval

  } // startSpinner()

  private stopSpinner(): void {
    this.spinner.hide(this.spinnerName);
    window.clearInterval(this.loadingId)
    this.loadingTimeout = 0
    this.loadingId = 0;
  }

  public get spinnerType(): string {
    return SpinnerTypes.BallClipRotate;
  }

  public get spinnerName(): string {
    return "dashboardLoading";
  }

  public showFullscreen(fit: boolean): void {

    if (this.elem.requestFullscreen) {
      this.elem.requestFullscreen();
      this.elem.addEventListener('fullscreenchange', () => { this.restoreDashboard() }, false);
    } else if (this.elem.mozRequestFullScreen) {
      /* Firefox */
      this.elem.mozRequestFullScreen();
      this.elem.addEventListener('mozfullscreenchange', () => { this.restoreDashboard() }, false);
    } else if (this.elem.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      this.elem.webkitRequestFullscreen();
      this.elem.addEventListener('webkitfullscreenchange', () => { this.restoreDashboard() }, false);
    } else if (this.elem.msRequestFullscreen) {
      /* IE/Edge */
      this.elem.msRequestFullscreen();
      this.elem.addEventListener('MSFullscreenChange', () => { this.restoreDashboard() }, false);
    }

    if (fit) {
      this.gridsterOptions.gridType = GridType.Fit
      this.gridsterOptions.minCols = 10
      this.gridsterOptions.minRows = 10
      if (this.gridsterOptions.api && this.gridsterOptions.api.optionsChanged) {
        this.gridsterOptions.api.optionsChanged()
      }
    }

  } // showFullscreen()

  private restoreDashboard() {
    if (this.document &&
      !this.document.fullscreenElement && !this.document.webkitIsFullScreen &&
      !this.document.webkitFullscreenElement && !this.document.msFullscreenElement) {
      this.gridsterOptions.gridType = GridType.ScrollVertical
      this.gridsterOptions.minCols = 100
      this.gridsterOptions.minRows = 100
      if (this.gridsterOptions.api && this.gridsterOptions.api.optionsChanged) {
        this.gridsterOptions.api.optionsChanged()
      }
    }
  } // restoreDashboard

} // class DashboardComponent
